import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { toast } from 'react-hot-toast'
import { CircleStackIcon } from '@heroicons/react/24/outline'
import { UserManagementService } from '@/services/apiClient'

export function OAuthCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { setUser, setToken } = useAuthStore()
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Prevent double execution in StrictMode or double mounting
    if (hasProcessed.current) {
      return
    }
    
    const handleCallback = async () => {
      hasProcessed.current = true
      try {
        const token = searchParams.get('token')
        const refreshToken = searchParams.get('refresh_token')
        const error = searchParams.get('error')

        if (error) {
          const errorMessage = error === 'oauth_failed' ? 'Google authentication failed' :
                             error === 'auth_failed' ? 'Authentication error' :
                             error === 'email_exists' ? 'Email already linked to another account' :
                             'Authentication failed'
          toast.error(errorMessage)
          navigate('/login')
          return
        }

        if (token) {
          // Store tokens in localStorage with correct keys
          localStorage.setItem('access_token', token)
          localStorage.setItem('token', token) // Also store as 'token' for compatibility
          if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken)
          }
          
          setToken(token)
          
          // Clean URL by removing token parameters
          const { pathname } = window.location
          const cleanUrl = pathname
          window.history.replaceState({}, document.title, cleanUrl)
          
          try {
            // Fetch user profile using the token
            const userProfile = await UserManagementService.getCurrentUser()
            
            // Store user data - map UserInfo to User type
            const user = {
              id: userProfile.id || '',
              username: userProfile.username || userProfile.email || '',
              email: userProfile.email || undefined,
              name: userProfile.fullName || userProfile.email?.split('@')[0] || '',
              role: 'user' as const,
              createdAt: new Date().toISOString(),
              authProvider: 'google' as const
            }
            localStorage.setItem('user', JSON.stringify(user))
            setUser(user)
            
            // Fetch user settings
            try {
              const { useSettingsStore } = await import('@/stores/settingsStore')
              await useSettingsStore.getState().fetchSettings()
              console.log('✅ [OAuth] User settings fetched after SSO login')
            } catch (settingsError) {
              console.error('Failed to fetch settings after SSO login:', settingsError)
            }
            
            // Check for stored redirect path or use default
            const storedRedirect = sessionStorage.getItem('oauth_redirect')
            if (storedRedirect) {
              sessionStorage.removeItem('oauth_redirect')
              navigate(storedRedirect)
            } else {
              navigate('/')
            }
          } catch (profileError) {
            console.error('Failed to fetch user profile:', profileError)
            toast.error('Failed to load user profile')
            navigate('/login')
          }
        } else {
          toast.error('Invalid authentication response')
          navigate('/login')
        }
      } catch (error) {
        console.error('OAuth callback error:', error)
        toast.error('Authentication failed')
        navigate('/login')
      }
    }

    void handleCallback()
  }, [searchParams, navigate, location, setUser, setToken])

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full">
              <CircleStackIcon className="w-10 h-10 text-primary-600" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Authenticating...
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Please wait while we complete your sign-in
          </p>
        </div>
      </div>
    </div>
  )
}